'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import React, { useEffect, useState, useRef } from 'react';
import { VariantProps } from 'tailwind-variants';

import ChevronIcon from '@/fo/icons/base/icons/chevron.svg';
import {
  UiKitBreadcrumbsBase,
  UiKitBreadcrumbsBaseProps
} from '@/fo/ui/kit/breadcrumbs/base';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitCarouselBase } from '@/fo/ui/kit/carousel/base';
import { UiKitPriceBase } from '@/fo/ui/kit/price/base';
import {
  UiModPictureBaseProps,
  UiModPictureBase
} from '@/fo/ui/mod/picture/base';
import { utilImageLoader } from '@/fo/util/image-loader';

import { eventBannerHero } from '../utils/event';

import { styles } from './UiKitBannerHero.styles';

type Variants = VariantProps<typeof styles>;

export interface Banner {
  imageSizes?: UiModPictureBaseProps['sizes'];
  image: {
    sm: string;
    md: string;
  };
  widthContent?: string;
  middleContent?: {
    image?: string;
    alt?: string;
    widthDesktop?: string | null;
    widthMobile?: string | null;
  };
  alt?: string;
  title: React.ReactNode;
  linkPageText?: string;
  subtitle?: string;
  description?: React.ReactNode;
  buttonText: string;
  link: string;
  dateExpire?: string;
  mode?: Variants['mode'];
  target?: boolean;
  buttons?: {
    id?: string | null;
    buttonLabel?: string | null;
    buttonHref?: string | null;
    buttonTarget?: string | null;
  }[];
}

export interface UiKitBannerHeroProps {
  children?: React.ReactNode;
  banners: Banner[];
  breadcrumbs?: UiKitBreadcrumbsBaseProps;
  className?: string;
  type?: Variants['type'];
  mode?: Variants['mode'];
  speed?: number;
  alignText?: Variants['alignText'];
  alignHeight?: Variants['alignHeight'];
  bannerMain?: Variants['bannerMain'];
  productPrice?: string;
  bannerOverlay?: boolean;
  date?: string;
  containerClassName?: string;
  bannerClassName?: string;
}

export function UiKitBannerHero({
  banners,
  breadcrumbs,
  date,
  className,
  type,
  mode,
  speed,
  alignText,
  alignHeight,
  bannerMain,
  productPrice,
  bannerOverlay = true,
  containerClassName,
  bannerClassName,
  ...props
}: UiKitBannerHeroProps) {
  const [currentBanner, setCurrentBanner] = useState<Banner | null>(banners[0]);
  const slots = styles({
    className,
    type,
    mode: currentBanner?.mode || mode,
    alignText,
    alignHeight,
    bannerMain
  });

  const bannerRef = useRef<HTMLDivElement>(null);
  const t = useTranslations();

  useEffect(() => {
    if (type === 'main') {
      eventBannerHero.setMode(currentBanner?.mode || 'off');
    }
  }, [currentBanner, type]);

  useEffect(() => {
    return () => {
      eventBannerHero.setMode('off');
    };
  }, []);

  const handleScrollToBannerBottom = () => {
    const target = bannerRef.current;

    if (!target) return;

    const destination = bannerRef.current?.nextElementSibling;

    destination?.scrollIntoView({ behavior: 'smooth' });
  };

  const heroBanner = type === 'main';

  const bannerButton = (banner: Banner) => {
    if (type !== 'category' && banner.link && banner.linkPageText) {
      return (
        <UiKitButtonBase
          as="link"
          className={slots.button()}
          href={banner.link}
          color={banner.mode === 'dark' ? 'secondary' : 'tertiary'}
          target={banner.target ? '_blank' : '_self'}
        >
          {banner.linkPageText}
        </UiKitButtonBase>
      );
    }

    if (
      banner.buttonText &&
      !banner.linkPageText &&
      type !== 'category' &&
      banner.buttonText !== t('Buy') &&
      !productPrice
    ) {
      return (
        <>
          {banner.link === '#' ? (
            <UiKitButtonBase
              as="button"
              className={slots.button()}
              color={banner.mode === 'dark' ? 'secondary' : 'tertiary'}
              onClick={() => handleScrollToBannerBottom()}
            >
              {banner.buttonText}
            </UiKitButtonBase>
          ) : (
            <UiKitButtonBase
              as="link"
              className={slots.button()}
              href={banner.link || '/'}
              color={banner.mode === 'dark' ? 'secondary' : 'tertiary'}
              target={banner.target ? '_blank' : '_self'}
            >
              {banner.buttonText}
            </UiKitButtonBase>
          )}
        </>
      );
    }

    if (
      banner.buttonText &&
      !banner.linkPageText &&
      type !== 'info' &&
      type !== 'category' &&
      banner.buttonText === t('Buy') &&
      productPrice
    ) {
      return (
        <UiKitButtonBase
          as="button"
          className={slots.button()}
          color={banner.mode === 'dark' ? 'secondary' : 'tertiary'}
          onClick={() => handleScrollToBannerBottom()}
        >
          {banner.buttonText} {t('from')}&nbsp;
          <UiKitPriceBase value={Number(productPrice)} />
        </UiKitButtonBase>
      );
    }

    if (banner.buttonText && !banner.linkPageText && type === 'info') {
      <Link
        href={banner.link || '/'}
        className={slots.link()}
      >
        <span>{banner.buttonText}</span>
        <ChevronIcon className={slots.arrow()} />
      </Link>;
    }

    return <></>;
  };

  return (
    <div
      ref={bannerRef}
      className={`${heroBanner ? 'banner-hero' : ''} ${slots.base({ className })}`}
      {...props}
    >
      <div className={slots.breadcrumbsWrapper()}>
        {!!breadcrumbs && (
          <UiKitBreadcrumbsBase
            items={breadcrumbs.items}
            className={slots.breadcrumbs()}
            type={mode}
          />
        )}
      </div>

      <UiKitCarouselBase
        options={{ active: banners.length > 1, watchDrag: true }}
        className={slots.carousel()}
        dotsClass={slots.dots()}
        arrowPrevClass={slots.arrowPrev()}
        arrowNextClass={slots.arrowNext()}
        containerClassName={containerClassName}
        classNameIcon={slots.arrowIcons()}
        autoplay={speed}
        showDots
        fade
        arrowType="chevron"
        onSlidesChange={(activeIndex) => {
          setCurrentBanner(banners[activeIndex]);
        }}
      >
        {banners.map((banner, index) => (
          <div
            key={index}
            className={`${slots.banner({ className: bannerClassName })} ${bannerOverlay ? 'after:bg-[#10060166]' : 'after:bg-transparent'}`}
          >
            <div className={slots.middleContentWrapper()}>
              {banner.middleContent?.image && (
                <div
                  className={slots.middleContent()}
                  style={
                    {
                      ...(banner.middleContent.widthMobile && {
                        '--middle-content-max-width':
                          banner.middleContent.widthMobile
                      }),
                      ...(banner.middleContent.widthDesktop && {
                        '--middle-content-max-width-lg':
                          banner.middleContent.widthDesktop
                      })
                    } as React.CSSProperties
                  }
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    className={slots.middleContentImage()}
                    src={banner.middleContent.image}
                    alt={banner.middleContent.alt || ''}
                  />
                </div>
              )}
            </div>

            {banner.imageSizes && (
              <UiModPictureBase
                imageClassName={slots.image()}
                sizes={banner.imageSizes}
                breakpoints={{
                  desktop: 1440,
                  tablet: 1024,
                  mobile: 600,
                  mobileSmall: 375
                }}
                alt={banner.alt || ''}
              />
            )}
            {!banner.imageSizes && banner.image.md && banner.image.sm && (
              <picture>
                <source
                  srcSet={utilImageLoader({
                    src: banner.image.sm,
                    width: 375,
                    quality: 100
                  })}
                  media="(max-width: 767px)"
                />
                <Image
                  className={slots.image()}
                  src={banner.image.md}
                  alt={banner.alt || ''}
                  width={1440}
                  height={360}
                />
              </picture>
            )}

            {date && <span className={slots.date()}>{date}</span>}
            {!!banner.dateExpire && (
              <span className={slots.dateExpire()}>{banner.dateExpire}</span>
            )}

            <div className={slots.wrapper()}>
              <div
                className={slots.content()}
                style={{ maxWidth: banner.widthContent }}
              >
                <h2 className={slots.title()}>{banner.title}</h2>
                {Boolean(banner?.subtitle) && (
                  <p className={slots.text()}>{banner.subtitle}</p>
                )}
                {Boolean(banner?.description) && (
                  <p className={slots.description()}>{banner.description}</p>
                )}
                {bannerButton(banner)}
                {type === 'category' &&
                  banner?.buttons &&
                  banner?.buttons?.length > 0 && (
                    <div
                      className={slots.buttonWrapper({
                        className: banner?.buttons?.length === 1 ? 'flex' : ''
                      })}
                    >
                      {banner?.buttons.map(
                        (button) =>
                          button.buttonLabel && (
                            <UiKitButtonBase
                              key={button.id ?? button.buttonHref}
                              as="link"
                              className={slots.button()}
                              href={button.buttonHref || '/'}
                              color={
                                banner.mode === 'dark'
                                  ? 'secondary'
                                  : 'tertiary'
                              }
                              target={button.buttonTarget ?? '_blank'}
                            >
                              {button.buttonLabel}
                            </UiKitButtonBase>
                          )
                      )}
                    </div>
                  )}
              </div>
            </div>
          </div>
        ))}
      </UiKitCarouselBase>
    </div>
  );
}
